import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { fetchWeather, fetchClimateNormals, compareToNormal, getNormalForDate, getWeekTrend, startOfWeek, DAY_SHORT, getWeatherMeta, getAqiInfo, getTourClimateNotes } from '../../utils/weatherService';
import { MAPBOX_ACCESS_TOKEN } from '../../config/mapbox-config';

function shortLabel(label = '') {
  const map = {
    'Clear Sky': 'Clear', 'Mostly Clear': 'Clear', 'Partly Cloudy': 'P. Cloudy',
    'Overcast': 'Overcast', 'Foggy': 'Fog', 'Icy Fog': 'Fog',
    'Light Drizzle': 'Drizzle', 'Drizzle': 'Drizzle', 'Heavy Drizzle': 'Drizzle',
    'Light Rain': 'Rain', 'Rain': 'Rain', 'Heavy Rain': 'Rain',
    'Light Snow': 'Snow', 'Snow': 'Snow', 'Heavy Snow': 'Snow', 'Snow Grains': 'Snow',
    'Light Showers': 'Showers', 'Showers': 'Showers', 'Heavy Showers': 'Showers',
    'Snow Showers': 'Snow', 'Heavy Snow Showers': 'Snow',
    'Thunderstorm': 'Thunder', 'Thunderstorm + Hail': 'Thunder', 'Severe Thunderstorm': 'Thunder',
  };
  return map[label] || label.split(' ').slice(0, 2).join(' ');
}

async function reverseGeocode(lat, lng) {
  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
      `?access_token=${MAPBOX_ACCESS_TOKEN}&types=place,region&limit=1&language=en`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = data.features?.[0];
    if (!feature) return null;
    // Build "City, State" or "City, Country"
    const placeName = feature.text || '';
    const context = feature.context || [];
    const region = context.find(c => c.id?.startsWith('region'))?.text || '';
    const country = context.find(c => c.id?.startsWith('country'))?.text || '';
    if (placeName && region) return `${placeName}, ${region}`;
    if (placeName && country) return `${placeName}, ${country}`;
    return placeName || feature.place_name?.split(',')[0] || null;
  } catch {
    return null;
  }
}

// Glass design tokens — adapts to dark/light mode
function makeTokens(dark) {
  return dark ? {
    bg:         'rgba(6, 9, 22, 0.52)',
    border:     'rgba(255,255,255,0.14)',
    divider:    'rgba(255,255,255,0.09)',
    textPri:    'rgba(255,255,255,0.96)',
    textSec:    'rgba(255,255,255,0.58)',
    textMuted:  'rgba(255,255,255,0.36)',
    btnBg:      'rgba(255,255,255,0.10)',
    btnBorder:  'rgba(255,255,255,0.13)',
    radarBg:    'rgba(38,132,255,0.32)',
    radarBorder:'rgba(38,132,255,0.5)',
    radarText:  '#60a5fa',
  } : {
    bg:         'rgba(255,255,255,0.88)',
    border:     'rgba(0,0,0,0.10)',
    divider:    'rgba(0,0,0,0.07)',
    textPri:    'rgba(0,0,0,0.88)',
    textSec:    'rgba(0,0,0,0.52)',
    textMuted:  'rgba(0,0,0,0.32)',
    btnBg:      'rgba(0,0,0,0.06)',
    btnBorder:  'rgba(0,0,0,0.10)',
    radarBg:    'rgba(38,132,255,0.15)',
    radarBorder:'rgba(38,132,255,0.4)',
    radarText:  '#2563eb',
  };
}

/**
 * Glassmorphic floating weather panel over the Mapbox map.
 * Collapsed by default, expandable via header tap. No close button.
 *
 * Props:
 *   coords        { lat, lng }
 *   radarEnabled  bool
 *   onToggleRadar fn
 */
export default function WeatherPanel({ coords, radarEnabled, onToggleRadar, isMobile, isDarkMode, onClose, panelStyle, onPanelLayout, expanded: expandedProp, onExpandedChange, fillContainer = false, compactMobile = false }) {
  const G = makeTokens(isDarkMode !== false);
  const styles = useMemo(() => makeStyles(G, compactMobile), [isDarkMode, compactMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  const [weatherData, setWeatherData]   = useState(null);
  const [aqiData, setAqiData]           = useState(null);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [expandedInternal, setExpandedInternal] = useState(false);
  const [weatherView, setWeatherView] = useState('forecast'); // 'forecast' | 'today' | 'trends'
  const [climateNormals, setClimateNormals] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState(null);
  const [trendGranularity, setTrendGranularity] = useState('week'); // 'week' | 'month' | 'day'
  const [trendRefDate, setTrendRefDate] = useState(() => new Date());
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedInternal;
  const setExpanded = (val) => {
    const next = typeof val === 'function' ? val(expanded) : val;
    if (isControlled) { onExpandedChange?.(next); } else { setExpandedInternal(next); }
  };
  const fetchedCoordsRef                = useRef(null);

  const load = useCallback(async () => {
    if (!coords?.lat || !coords?.lng) return;
    const prev = fetchedCoordsRef.current;
    if (prev) {
      if (Math.abs(prev.lat - coords.lat) < 0.05 && Math.abs(prev.lng - coords.lng) < 0.05) return;
    }
    setLoading(true);
    setError(null);
    try {
      const [{ weather, aqi }, geoName] = await Promise.all([
        fetchWeather(coords.lat, coords.lng),
        reverseGeocode(coords.lat, coords.lng),
      ]);
      setWeatherData(weather);
      setAqiData(aqi);
      setLocationName(geoName || '');
      fetchedCoordsRef.current = { lat: coords.lat, lng: coords.lng };
    } catch {
      setError('Could not load weather data.');
    } finally {
      setLoading(false);
    }
  }, [coords?.lat, coords?.lng]);

  useEffect(() => { load(); }, [load]);

  const trendsFetchedKeyRef = useRef(null);
  const trendsInFlightRef = useRef(false);
  const trendsDebounceRef = useRef(null);

  const loadTrends = useCallback(async () => {
    if (!coords?.lat || !coords?.lng) return;
    // Round to ~0.1° so small pans don't re-trigger a heavy 30-year archive fetch
    const key = `${coords.lat.toFixed(1)},${coords.lng.toFixed(1)}`;
    if (trendsFetchedKeyRef.current === key) return; // already have normals for these coords
    if (trendsInFlightRef.current) return; // a fetch is already running
    trendsInFlightRef.current = true;
    setTrendsLoading(true);
    setTrendsError(null);
    try {
      const normals = await fetchClimateNormals(coords.lat, coords.lng);
      setClimateNormals(normals);
      trendsFetchedKeyRef.current = key; // only cache the key on success → failures can retry
    } catch {
      setTrendsError('Could not load climate trends. (Rate-limited? Try again in a moment.)');
      setClimateNormals(null);
    } finally {
      setTrendsLoading(false);
      trendsInFlightRef.current = false;
    }
  }, [coords?.lat, coords?.lng]);

  // Debounce: rapid panning coalesces into one fetch per ~0.6s of stillness.
  useEffect(() => {
    if (weatherView !== 'trends') return;
    if (trendsDebounceRef.current) clearTimeout(trendsDebounceRef.current);
    trendsDebounceRef.current = setTimeout(() => { loadTrends(); }, 600);
    return () => { if (trendsDebounceRef.current) clearTimeout(trendsDebounceRef.current); };
  }, [weatherView, loadTrends]);

  const current     = weatherData?.current;
  const daily       = weatherData?.daily;
  const aqiVal      = aqiData?.current?.us_aqi;
  const aqiInfo     = getAqiInfo(aqiVal);
  const weatherMeta = current ? getWeatherMeta(current.weather_code) : null;

  const displayLocation = locationName || (coords ? 'Map Center' : '—');
  const forecastHighLow = daily?.time?.length
    ? `${Math.round(daily.temperature_2m_max[0])}° / ${Math.round(daily.temperature_2m_min[0])}°`
    : null;

  return (
    <View
      style={[styles.panel, isMobile && styles.panelMobile, panelStyle]}
      onLayout={(e) => onPanelLayout?.(e.nativeEvent.layout)}
    >

      {/* ── Header ── */}
      <TouchableOpacity
        style={styles.header}
        onPress={() => setExpanded(e => !e)}
        activeOpacity={0.75}
      >
        <View style={styles.headerLeft}>
          {/* Location pin dot */}
          <View style={styles.locationDot} />
          <View style={styles.headerTextBlock}>
            <Text style={styles.locationName} numberOfLines={1}>
              {displayLocation}
            </Text>
            <Text style={styles.headerConditionLine} numberOfLines={1}>
              {loading && !daily
                ? 'Loading weather…'
                : weatherView === 'today' && current
                  ? `${Math.round(current.temperature_2m)}°F  ·  ${weatherMeta?.label ?? ''}`
                  : forecastHighLow
                    ? `7-day  ·  Today ${forecastHighLow}`
                    : loading
                      ? 'Updating weather…'
                      : 'Weather'}
            </Text>
          </View>
        </View>

        <View style={styles.headerActions}>
          <TouchableOpacity
            style={[styles.pill, radarEnabled && styles.pillActive]}
            onPress={onToggleRadar}
            hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
          >
            <Text style={[styles.pillText, radarEnabled && styles.pillTextActive]}>Radar</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.iconBtn}
            onPress={(e) => { e.stopPropagation?.(); fetchedCoordsRef.current = null; load(); }}
            hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
          >
            <Text style={styles.refreshText}>↺</Text>
          </TouchableOpacity>

          <Text style={styles.chevron}>{expanded ? '▲' : '▼'}</Text>

          {onClose && (
            <TouchableOpacity
              style={styles.closeBtn}
              onPress={(e) => { e.stopPropagation?.(); onClose(); }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>

      {/* ── Expanded body ── */}
      {expanded && (
        <View style={isMobile ? styles.expandedBodyMobile : undefined}>
          <View style={styles.viewTabs}>
            {[
              { id: 'forecast', label: '7-Day' },
              { id: 'today', label: 'Today' },
              { id: 'trends', label: 'Trends' },
            ].map((tab) => (
              <TouchableOpacity
                key={tab.id}
                style={[styles.viewTab, weatherView === tab.id && styles.viewTabActive]}
                onPress={() => setWeatherView(tab.id)}
              >
                <Text style={[styles.viewTabText, weatherView === tab.id && styles.viewTabTextActive]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <WeatherPanelBody
            loading={loading}
            error={error}
            current={current}
            daily={daily}
            aqiVal={aqiVal}
            aqiInfo={aqiInfo}
            weatherMeta={weatherMeta}
            radarEnabled={radarEnabled}
            weatherView={weatherView}
            climateNormals={climateNormals}
            trendsLoading={trendsLoading}
            trendsError={trendsError}
            trendGranularity={trendGranularity}
            setTrendGranularity={setTrendGranularity}
            trendRefDate={trendRefDate}
            setTrendRefDate={setTrendRefDate}
            G={G}
            styles={styles}
            compactMobile={compactMobile}
            isMobile={isMobile}
          />
        </View>
      )}
    </View>
  );
}

function getTrendsMaxHeight(windowHeight, { isMobile = false, compactMobile = false } = {}) {
  const h = windowHeight || 800;
  if (compactMobile) return Math.min(220, Math.max(150, Math.round(h * 0.28)));
  if (isMobile) return Math.min(280, Math.max(180, Math.round(h * 0.34)));
  // Desktop floating panel — cap so it doesn't cover the map
  if (h < 700) return Math.min(280, Math.max(180, Math.round(h * 0.38)));
  if (h < 900) return Math.min(340, Math.round(h * 0.40));
  return Math.min(400, Math.round(h * 0.42));
}

function WeatherPanelBody({
  loading, error, current, daily, aqiVal, aqiInfo, weatherMeta, radarEnabled,
  weatherView, climateNormals, trendsLoading, trendsError,
  trendGranularity, setTrendGranularity, trendRefDate, setTrendRefDate,
  G, styles, compactMobile = false, isMobile = false,
}) {
  const { height: windowHeight } = useWindowDimensions();
  const trendsMaxHeight = getTrendsMaxHeight(windowHeight, { isMobile, compactMobile });

  const renderForecast = () => (
    daily?.time ? (
      <View style={[styles.forecastRow, compactMobile && styles.forecastRowCompact, { width: '100%' }]}>
        {daily.time.slice(0, 7).map((dateStr, i) => {
          const d = new Date(dateStr + 'T12:00:00');
          const dayLabel = i === 0 ? 'Today' : DAY_SHORT[d.getDay()];
          const meta = getWeatherMeta(daily.weather_code[i]);
          const normal = getNormalForDate(d, climateNormals);
          const trend = compareToNormal(daily.temperature_2m_max[i], normal);
          const precip = daily.precipitation_sum?.[i];
          const rain = daily.rain_sum?.[i];
          const snow = daily.snowfall_sum?.[i];
          const precipProb = daily.precipitation_probability_max?.[i];
          const feels = daily.apparent_temperature_max?.[i];
          const wind = daily.wind_speed_10m_max?.[i];
          return (
            <View
              key={dateStr}
              style={[styles.dayCard, compactMobile && styles.dayCardCompact, i < 6 && styles.dayCardBorder]}
            >
              <Text style={[styles.dayLabel, compactMobile && styles.dayLabelCompact]}>{dayLabel}</Text>
              <Text style={[styles.dayCondition, compactMobile && styles.dayConditionCompact]} numberOfLines={2}>
                {shortLabel(meta.label)}
              </Text>
              <Text style={[styles.dayHigh, compactMobile && styles.dayHighCompact]}>{Math.round(daily.temperature_2m_max[i])}°</Text>
              <Text style={[styles.dayLow, compactMobile && styles.dayLowCompact]}>{Math.round(daily.temperature_2m_min[i])}°</Text>
              {precipProb != null && (
                <Text style={styles.dayPrecipHint}>{Math.round(precipProb)}% rain</Text>
              )}
              {precip != null && precip > 0 && (
                <Text style={styles.dayPrecipHint}>{Number(precip).toFixed(2)}"</Text>
              )}
              {snow != null && snow > 0 && (
                <Text style={styles.dayPrecipHint}>Snow {Number(snow).toFixed(1)}"</Text>
              )}
              {rain != null && rain > 0 && !(snow > 0) && precipProb == null && (
                <Text style={styles.dayPrecipHint}>Rain {Number(rain).toFixed(2)}"</Text>
              )}
              {feels != null && (
                <Text style={styles.dayMetaHint}>Feels {Math.round(feels)}°</Text>
              )}
              {wind != null && (
                <Text style={styles.dayMetaHint}>{Math.round(wind)} mph</Text>
              )}
              {trend && (
                <Text style={styles.dayTrendHint} numberOfLines={2}>{trend.label}</Text>
              )}
            </View>
          );
        })}
      </View>
    ) : (
      <Text style={styles.emptyText}>No forecast available.</Text>
    )
  );

  const renderToday = () => (
    current ? (
      <>
        <View style={styles.currentSection}>
          <View style={styles.currentLeft}>
            <Text style={styles.bigTemp}>{Math.round(current.temperature_2m)}°</Text>
            <Text style={styles.conditionLabel}>{weatherMeta?.label}</Text>
            <Text style={styles.feelsLike}>Feels like {Math.round(current.apparent_temperature)}°</Text>
          </View>
          <View style={styles.currentRight}>
            <MetaRow label="Wind"     value={`${Math.round(current.wind_speed_10m)} mph`} s={styles} />
            {current.wind_gusts_10m != null && (
              <MetaRow label="Gusts" value={`${Math.round(current.wind_gusts_10m)} mph`} s={styles} />
            )}
            <MetaRow label="Humidity" value={`${current.relative_humidity_2m}%`} s={styles} />
            <MetaRow label="Precip"   value={`${Number(current.precipitation || 0).toFixed(2)} in`} s={styles} />
            {current.surface_pressure != null && (
              <MetaRow label="Pressure" value={`${Math.round(current.surface_pressure)} hPa`} s={styles} />
            )}
            {current.cloud_cover != null && (
              <MetaRow label="Clouds" value={`${Math.round(current.cloud_cover)}%`} s={styles} />
            )}
          </View>
        </View>
        {aqiVal != null && (
          <View style={[styles.aqiRow, { backgroundColor: aqiInfo.bg, borderColor: aqiInfo.color + '50' }]}>
            <Text style={[styles.aqiLeft, { color: aqiInfo.color }]}>
              Air Quality  ·  US AQI {aqiVal}
            </Text>
            <Text style={[styles.aqiStatus, { color: aqiInfo.color }]}>{aqiInfo.label}</Text>
          </View>
        )}
      </>
    ) : (
      <Text style={styles.emptyText}>No current conditions available.</Text>
    )
  );

  const fmtDate = (d) => `${DAY_SHORT[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;

  const stepRef = (days) => {
    const d = new Date(trendRefDate);
    d.setDate(d.getDate() + days);
    setTrendRefDate(d);
  };

  const renderClimateMetricGrid = (row, { monthly = false } = {}) => {
    if (!row) return null;
    const chips = [
      row.precip != null && { label: monthly ? 'Precip / mo' : 'Avg precip', value: `${row.precip.toFixed(monthly ? 1 : 2)}"` },
      row.rain != null && { label: 'Rain', value: `${row.rain.toFixed(monthly ? 1 : 2)}"` },
      row.snow != null && row.snow > 0 && { label: 'Snow', value: `${row.snow.toFixed(monthly ? 1 : 2)}"` },
      row.feelsHigh != null && { label: 'Feels high', value: `${Math.round(row.feelsHigh)}°` },
      row.windMax != null && { label: 'Max wind', value: `${Math.round(row.windMax)} mph` },
      row.precipHours != null && { label: monthly ? 'Wet hours / mo' : 'Wet hours', value: `${row.precipHours}` },
      monthly && row.wetDays != null && { label: 'Rainy days', value: `~${row.wetDays}` },
      monthly && row.heavyRainDays != null && row.heavyRainDays > 0 && { label: 'Heavy rain days', value: `~${row.heavyRainDays}`, warn: row.heavyRainDays >= 1.5 },
      monthly && row.snowDays != null && row.snowDays > 0 && { label: 'Snow days', value: `~${row.snowDays}` },
      monthly && row.heatDays != null && row.heatDays > 0 && { label: 'Hot days', value: `~${row.heatDays}`, warn: row.heatDays >= 5 },
      monthly && row.stormDays != null && row.stormDays > 0 && { label: 'Storm days', value: `~${row.stormDays}`, warn: row.stormDays >= 2 },
      !monthly && row.wetDays != null && { label: 'Wet chance', value: `${Math.round(Math.min(1, row.wetDays) * 100)}%` },
      !monthly && row.heavyRainDays != null && row.heavyRainDays > 0 && { label: 'Heavy-rain chance', value: `${Math.round(Math.min(1, row.heavyRainDays) * 100)}%`, warn: true },
    ].filter(Boolean);

    if (!chips.length) return null;
    return (
      <View style={styles.climateChipGrid}>
        {chips.map((chip) => (
          <View
            key={chip.label}
            style={[styles.climateChip, chip.warn && styles.climateChipWarn]}
          >
            <Text style={[styles.climateChipLabel, chip.warn && styles.climateChipLabelWarn]}>{chip.label}</Text>
            <Text style={[styles.climateChipValue, chip.warn && styles.climateChipValueWarn]}>{chip.value}</Text>
          </View>
        ))}
      </View>
    );
  };

  const renderTourNotes = (monthRow) => {
    const notes = getTourClimateNotes(monthRow, climateNormals?.thresholds);
    if (!notes.length) return null;
    return (
      <View style={styles.tourNotesBlock}>
        {notes.map((note) => (
          <View
            key={note.id}
            style={[styles.tourNoteRow, note.tone === 'warn' ? styles.tourNoteWarn : styles.tourNoteInfo]}
          >
            <Text style={[styles.tourNoteText, note.tone === 'warn' ? styles.tourNoteTextWarn : null]}>
              {note.text}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderTrendControls = () => (
    <View style={styles.trendsControls}>
      <View style={styles.trendsGranularityRow}>
        {[
          { id: 'week', label: 'Week' },
          { id: 'month', label: 'Month' },
          { id: 'day', label: 'Day' },
        ].map((g) => (
          <TouchableOpacity
            key={g.id}
            style={[styles.trendsGranularityBtn, trendGranularity === g.id && styles.trendsGranularityBtnActive]}
            onPress={() => setTrendGranularity(g.id)}
          >
            <Text style={[styles.trendsGranularityBtnText, trendGranularity === g.id && styles.trendsGranularityBtnTextActive]}>
              {g.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      {trendGranularity !== 'month' && (
        <View style={styles.trendsStepperRow}>
          <TouchableOpacity style={styles.trendsStepperBtn} onPress={() => stepRef(trendGranularity === 'day' ? -1 : -7)}>
            <Text style={styles.trendsStepperBtnText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.trendsStepperLabel}>
            {trendGranularity === 'day' ? fmtDate(trendRefDate) : `Week of ${fmtDate(startOfWeek(trendRefDate))}`}
          </Text>
          <TouchableOpacity style={styles.trendsStepperBtn} onPress={() => stepRef(trendGranularity === 'day' ? 1 : 7)}>
            <Text style={styles.trendsStepperBtnText}>›</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  const renderWeekTrend = () => {
    const days = getWeekTrend(trendRefDate, climateNormals);
    const forecastByDate = {};
    if (daily?.time) {
      daily.time.forEach((ds, i) => { forecastByDate[ds] = i; });
    }
    const weekPrecip = days.reduce((sum, { normal }) => sum + (normal?.precip || 0), 0);
    const weekHeavyExpected = days.reduce((sum, { normal }) => sum + (normal?.heavyRainDays || 0), 0);
    const weekSnow = days.reduce((sum, { normal }) => sum + (normal?.snow || 0), 0);
    return (
      <View style={styles.trendsList}>
        {renderTourNotes(climateNormals.monthly.find((m) => m.monthIndex === trendRefDate.getMonth()))}
        <View style={styles.trendsHighlight}>
          <Text style={styles.trendsHighlightTitle}>Typical week snapshot</Text>
          <Text style={styles.trendsHighlightSub}>
            ~{weekPrecip.toFixed(2)}" precip
            {weekSnow > 0 ? ` · ~${weekSnow.toFixed(2)}" snow` : ''}
            {weekHeavyExpected > 0.2 ? ` · ~${weekHeavyExpected.toFixed(1)} expected heavy-rain days` : ''}
          </Text>
        </View>
        {days.map(({ date, label, normal }) => {
          const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const fi = forecastByDate[ds];
          const hasForecast = fi != null;
          const fHigh = hasForecast ? Math.round(daily.temperature_2m_max[fi]) : null;
          const fPrecip = hasForecast && daily.precipitation_sum ? daily.precipitation_sum[fi] : null;
          const cmp = hasForecast && normal ? compareToNormal(fHigh, normal) : null;
          return (
            <View key={ds} style={styles.trendsWeekRow}>
              <View style={styles.trendsWeekDay}>
                <Text style={styles.trendsWeekDayLabel}>{label}</Text>
                <Text style={styles.trendsWeekDate}>{date.getMonth() + 1}/{date.getDate()}</Text>
              </View>
              <View style={styles.trendsWeekNormal}>
                <Text style={styles.trendsWeekNormalLabel}>Typical</Text>
                <Text style={styles.trendsWeekNormalValue}>
                  {normal ? `${Math.round(normal.high)}° / ${Math.round(normal.low)}°` : '—'}
                </Text>
                {normal?.precip != null && (
                  <Text style={styles.trendsWeekMeta}>
                    {normal.precip.toFixed(2)}"
                    {normal.snow > 0 ? ` · snow ${normal.snow.toFixed(2)}"` : ''}
                    {(normal.heavyRainDays || 0) > 0.15 ? ' · heavy risk' : ''}
                  </Text>
                )}
              </View>
              <View style={styles.trendsWeekForecast}>
                <Text style={styles.trendsWeekForecastLabel}>{hasForecast ? 'Forecast' : ''}</Text>
                <Text style={styles.trendsWeekForecastValue}>
                  {hasForecast ? `${fHigh}°` : '—'}
                </Text>
                {fPrecip != null && (
                  <Text style={styles.trendsWeekMeta}>{Number(fPrecip).toFixed(2)}" fx</Text>
                )}
                {cmp && <Text style={styles.trendsWeekCmp}>{cmp.label}</Text>}
              </View>
            </View>
          );
        })}
      </View>
    );
  };

  const renderDayTrend = () => {
    const normal = getNormalForDate(trendRefDate, climateNormals);
    if (!normal) return <Text style={styles.emptyText}>No normal available for this date.</Text>;
    const ds = `${trendRefDate.getFullYear()}-${String(trendRefDate.getMonth() + 1).padStart(2, '0')}-${String(trendRefDate.getDate()).padStart(2, '0')}`;
    const fi = daily?.time ? daily.time.indexOf(ds) : -1;
    const hasForecast = fi >= 0;
    const fHigh = hasForecast ? Math.round(daily.temperature_2m_max[fi]) : null;
    const cmp = hasForecast ? compareToNormal(fHigh, normal) : null;
    return (
      <View style={styles.trendsList}>
        {renderTourNotes(climateNormals.monthly.find((m) => m.monthIndex === trendRefDate.getMonth()))}
        <View style={styles.trendsHighlight}>
          <Text style={styles.trendsHighlightTitle}>Typical for {fmtDate(trendRefDate)}</Text>
          <Text style={styles.trendsHighlightValue}>
            High {Math.round(normal.high)}°  ·  Low {Math.round(normal.low)}°
          </Text>
          {hasForecast && (
            <Text style={styles.trendsHighlightSub}>
              Forecast high {fHigh}°  ·  {cmp ? cmp.label : ''}
            </Text>
          )}
        </View>
        {renderClimateMetricGrid(normal, { monthly: false })}
      </View>
    );
  };

  const renderMonthTrend = () => {
    const currentMonth = trendRefDate.getMonth();
    const thisMonth = climateNormals.monthly.find((m) => m.monthIndex === currentMonth);
    return (
      <View style={styles.trendsList}>
        {renderTourNotes(thisMonth)}
        {thisMonth && (
          <View style={styles.trendsHighlight}>
            <Text style={styles.trendsHighlightTitle}>Typical for {thisMonth.month}</Text>
            <Text style={styles.trendsHighlightValue}>
              High {Math.round(thisMonth.high)}°  ·  Low {Math.round(thisMonth.low)}°
            </Text>
            {thisMonth.feelsHigh != null && (
              <Text style={styles.trendsHighlightSub}>
                Feels-like high ~{Math.round(thisMonth.feelsHigh)}°
              </Text>
            )}
          </View>
        )}
        {renderClimateMetricGrid(thisMonth, { monthly: true })}
        <View style={styles.trendsGrid}>
          {climateNormals.monthly.map((row) => (
            <View key={row.month} style={[styles.trendsMonthRow, row.monthIndex === currentMonth && styles.trendsMonthRowActive]}>
              <Text style={styles.trendsMonthLabel}>{row.month}</Text>
              <Text style={styles.trendsMonthValue}>
                {Math.round(row.high)}° / {Math.round(row.low)}°
              </Text>
              <Text style={styles.trendsMonthMeta}>
                {(row.precip ?? 0).toFixed(1)}"
                {(row.heavyRainDays || 0) >= 1.5 ? ' · flood risk' : ''}
                {(row.snowDays || 0) >= 2 ? ' · snow' : ''}
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderTrends = () => {
    if (trendsLoading) {
      return (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={G.textSec} />
          <Text style={styles.loadingText}>Loading climate trends…</Text>
        </View>
      );
    }
    if (trendsError) return <Text style={styles.errorText}>{trendsError}</Text>;
    if (!climateNormals?.monthly?.length) return <Text style={styles.emptyText}>No trends data available.</Text>;

    return (
      <View style={styles.trendsSection}>
        <Text style={styles.trendsIntro}>
          1991–2020 climate normals for this map region — temps, rain, snow, heat, wind, and heavy-rain days (tour routing risk proxy). Switch week / month / day while planning a route.
        </Text>
        {renderTrendControls()}
        <ScrollView
          style={[styles.trendsScroll, { maxHeight: trendsMaxHeight }]}
          contentContainerStyle={styles.trendsScrollContent}
          nestedScrollEnabled
          showsVerticalScrollIndicator
          keyboardShouldPersistTaps="handled"
          // Web: thin custom scrollbar (see global.css .vrc-weather-trends-scroll)
          {...(typeof document !== 'undefined' ? { className: 'vrc-weather-trends-scroll' } : {})}
        >
          {trendGranularity === 'week' && renderWeekTrend()}
          {trendGranularity === 'month' && renderMonthTrend()}
          {trendGranularity === 'day' && renderDayTrend()}
        </ScrollView>
      </View>
    );
  };

  return (
    <>
      <View style={styles.divider} />

      {loading && weatherView !== 'trends' && (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={G.textSec} />
          <Text style={styles.loadingText}>Fetching weather…</Text>
        </View>
      )}

      {!loading && error && weatherView !== 'trends' && (
        <Text style={styles.errorText}>{error}</Text>
      )}

      {!loading && !error && weatherView === 'forecast' && renderForecast()}
      {!loading && !error && weatherView === 'today' && renderToday()}
      {weatherView === 'trends' && renderTrends()}

      {radarEnabled && (
        <View style={styles.radarHint}>
          <Text style={styles.radarHintText}>Live radar active</Text>
        </View>
      )}

      {!loading && !error && !current && weatherView === 'forecast' && !daily?.time && (
        <Text style={styles.emptyText}>No data available.</Text>
      )}
    </>
  );
}

function MetaRow({ label, value, s }) {
  return (
    <View style={s.metaRow}>
      <Text style={s.metaKey}>{label}</Text>
      <Text style={s.metaVal}>{value}</Text>
    </View>
  );
}

const makeStyles = (G, compactMobile = false) => StyleSheet.create({
  panel: {
    position: 'absolute',
    bottom: 12,
    left: 50,
    width: 370,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: G.border,
    backgroundColor: G.bg,
    backdropFilter: 'blur(28px) saturate(200%)',
    WebkitBackdropFilter: 'blur(28px) saturate(200%)',
    zIndex: 20,
    overflow: 'hidden',
    boxShadow: '0 12px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.12)',
    flexDirection: 'column',
  },
  panelMobile: {
    position: 'absolute',
    left: 10,
    right: 10,
    width: 'auto',
    maxWidth: 'calc(100vw - 20px)',
    borderRadius: 16,
    zIndex: 20,
  },
  expandedBodyMobile: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  // ── Header ──
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: compactMobile ? 7 : 9,
    gap: 9,
    flexShrink: 0,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    flex: 1,
    minWidth: 0,
  },
  locationDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#60a5fa',
    flexShrink: 0,
    marginTop: 1,
  },
  headerTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  locationName: {
    fontSize: 12,
    fontWeight: '700',
    color: G.textPri,
    letterSpacing: 0.1,
  },
  headerConditionLine: {
    fontSize: 10,
    color: G.textSec,
    marginTop: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  pill: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 5,
    backgroundColor: G.btnBg,
    borderWidth: 1,
    borderColor: G.btnBorder,
  },
  pillActive: {
    backgroundColor: G.radarBg,
    borderColor: G.radarBorder,
  },
  pillText: {
    fontSize: 10,
    fontWeight: '600',
    color: G.textSec,
    letterSpacing: 0.2,
  },
  pillTextActive: {
    color: G.radarText,
  },
  iconBtn: {
    width: 22,
    height: 22,
    borderRadius: 5,
    backgroundColor: G.btnBg,
    borderWidth: 1,
    borderColor: G.btnBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  refreshText: {
    fontSize: 12,
    color: G.textSec,
    lineHeight: 14,
  },
  chevron: {
    fontSize: 8,
    color: G.textMuted,
    marginLeft: 2,
  },
  closeBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: G.chip,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
  },
  closeBtnText: {
    fontSize: 10,
    color: G.textMuted,
    lineHeight: 22,
  },
  // ── Divider ──
  divider: {
    height: 1,
    backgroundColor: G.divider,
    marginHorizontal: 12,
  },
  // ── Loading / error / empty ──
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 14,
  },
  loadingText: {
    fontSize: 12,
    color: G.textSec,
  },
  errorText: {
    fontSize: 12,
    color: '#f87171',
    padding: 14,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 12,
    color: G.textSec,
    padding: 14,
    textAlign: 'center',
  },
  // ── Current conditions ──
  currentSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: compactMobile ? 10 : 14,
  },
  currentLeft: {
    gap: 3,
  },
  bigTemp: {
    fontSize: compactMobile ? 40 : 46,
    fontWeight: '200',
    color: G.textPri,
    lineHeight: compactMobile ? 44 : 50,
    letterSpacing: -2,
  },
  conditionLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: G.textSec,
    marginTop: 2,
  },
  feelsLike: {
    fontSize: 11,
    color: G.textMuted,
    marginTop: 3,
  },
  currentRight: {
    alignItems: 'flex-end',
    gap: 7,
    paddingTop: 10,
    flexShrink: 1,
    minWidth: 0,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  metaKey: {
    fontSize: 11,
    color: G.textSec,
    fontWeight: '500',
  },
  metaVal: {
    fontSize: 11,
    color: G.textPri,
    fontWeight: '600',
  },
  // ── AQI ──
  aqiRow: {
    marginHorizontal: 12,
    marginBottom: compactMobile ? 8 : 12,
    paddingHorizontal: 12,
    paddingVertical: compactMobile ? 6 : 8,
    borderRadius: 9,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  aqiLeft: {
    fontSize: 11,
    fontWeight: '600',
  },
  aqiStatus: {
    fontSize: 11,
    fontWeight: '700',
  },
  // ── Forecast ──
  forecastRow: {
    flexDirection: 'row',
    paddingHorizontal: 4,
    paddingVertical: compactMobile ? 8 : 12,
  },
  forecastRowCompact: {
    paddingVertical: 8,
  },
  dayCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 4,
    paddingVertical: 2,
    gap: 4,
    minWidth: 0,
  },
  dayCardCompact: {
    paddingHorizontal: 2,
    gap: 2,
  },
  dayCardBorder: {
    borderRightWidth: 1,
    borderRightColor: G.divider,
  },
  dayLabel: {
    fontSize: 9,
    fontWeight: '700',
    color: G.textSec,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  dayLabelCompact: {
    fontSize: 8,
  },
  dayCondition: {
    fontSize: 9,
    color: G.textMuted,
    textAlign: 'center',
    lineHeight: 12,
  },
  dayConditionCompact: {
    fontSize: 8,
    lineHeight: 10,
  },
  dayHigh: {
    fontSize: 13,
    fontWeight: '600',
    color: G.textPri,
  },
  dayHighCompact: {
    fontSize: 12,
  },
  dayLow: {
    fontSize: 11,
    color: G.textSec,
  },
  dayLowCompact: {
    fontSize: 10,
  },
  dayTrendHint: {
    fontSize: 8,
    color: G.textMuted,
    textAlign: 'center',
    lineHeight: 10,
    marginTop: 2,
  },
  dayPrecipHint: {
    fontSize: 8,
    color: G.radarText || G.textSec,
    textAlign: 'center',
    marginTop: 2,
  },
  dayMetaHint: {
    fontSize: 8,
    color: G.textMuted,
    textAlign: 'center',
    marginTop: 1,
  },
  viewTabs: {
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 4,
  },
  viewTab: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: G.btnBg,
    borderWidth: 1,
    borderColor: G.btnBorder,
  },
  viewTabActive: {
    backgroundColor: G.radarBg,
    borderColor: G.radarBorder,
  },
  viewTabText: {
    fontSize: 11,
    fontWeight: '600',
    color: G.textSec,
  },
  viewTabTextActive: {
    color: G.radarText,
  },
  trendsSection: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 10,
    maxHeight: '100%',
  },
  trendsScroll: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
    // Firefox / standards: slim scrollbar
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(128,128,128,0.45) transparent',
  },
  trendsScrollContent: {
    gap: 10,
    paddingBottom: 8,
    paddingRight: 4,
  },
  trendsIntro: {
    fontSize: 11,
    lineHeight: 16,
    color: G.textSec,
  },
  trendsHighlight: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: G.divider,
    backgroundColor: G.btnBg,
    padding: 10,
    gap: 4,
  },
  trendsHighlightTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: G.textSec,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  trendsHighlightValue: {
    fontSize: 16,
    fontWeight: '700',
    color: G.textPri,
  },
  trendsHighlightSub: {
    fontSize: 11,
    color: G.textMuted,
  },
  trendsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  trendsMonthRow: {
    width: '30%',
    minWidth: 88,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: G.divider,
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: G.btnBg,
  },
  trendsMonthLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: G.textSec,
  },
  trendsMonthValue: {
    fontSize: 11,
    color: G.textPri,
    marginTop: 2,
  },
  trendsMonthMeta: {
    fontSize: 9,
    color: G.textMuted,
    marginTop: 2,
  },
  trendsMonthRowActive: {
    borderColor: G.radarBorder,
    backgroundColor: G.radarBg,
  },
  trendsWeekMeta: {
    fontSize: 9,
    color: G.textMuted,
    marginTop: 2,
  },
  climateChipGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  climateChip: {
    minWidth: '30%',
    flexGrow: 1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: G.divider,
    backgroundColor: G.btnBg,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  climateChipWarn: {
    borderColor: G.radarBorder || '#c45c26',
    backgroundColor: G.radarBg || 'rgba(196,92,38,0.12)',
  },
  climateChipLabel: {
    fontSize: 9,
    fontWeight: '700',
    color: G.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  climateChipLabelWarn: {
    color: G.radarText || '#c45c26',
  },
  climateChipValue: {
    fontSize: 13,
    fontWeight: '700',
    color: G.textPri,
    marginTop: 2,
  },
  climateChipValueWarn: {
    color: G.radarText || '#c45c26',
  },
  tourNotesBlock: {
    gap: 6,
    marginBottom: 2,
  },
  tourNoteRow: {
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tourNoteWarn: {
    borderColor: G.radarBorder || '#c45c26',
    backgroundColor: G.radarBg || 'rgba(196,92,38,0.12)',
  },
  tourNoteInfo: {
    borderColor: G.divider,
    backgroundColor: G.btnBg,
  },
  tourNoteText: {
    fontSize: 11,
    lineHeight: 16,
    color: G.textPri,
  },
  tourNoteTextWarn: {
    color: G.radarText || G.textPri,
  },
  trendsControls: {
    gap: 8,
  },
  trendsGranularityRow: {
    flexDirection: 'row',
    gap: 6,
  },
  trendsGranularityBtn: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: G.btnBg,
    borderWidth: 1,
    borderColor: G.btnBorder,
  },
  trendsGranularityBtnActive: {
    backgroundColor: G.radarBg,
    borderColor: G.radarBorder,
  },
  trendsGranularityBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: G.textSec,
  },
  trendsGranularityBtnTextActive: {
    color: G.radarText,
  },
  trendsStepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  trendsStepperBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: G.btnBg,
    borderWidth: 1,
    borderColor: G.btnBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  trendsStepperBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: G.textPri,
    lineHeight: 18,
  },
  trendsStepperLabel: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '600',
    color: G.textPri,
  },
  trendsList: {
    gap: 4,
  },
  trendsWeekRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: G.divider,
    backgroundColor: G.btnBg,
    gap: 8,
  },
  trendsWeekDay: {
    width: 52,
  },
  trendsWeekDayLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: G.textPri,
  },
  trendsWeekDate: {
    fontSize: 9,
    color: G.textMuted,
    marginTop: 1,
  },
  trendsWeekNormal: {
    flex: 1,
  },
  trendsWeekNormalLabel: {
    fontSize: 9,
    color: G.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  trendsWeekNormalValue: {
    fontSize: 12,
    fontWeight: '600',
    color: G.textPri,
    marginTop: 1,
  },
  trendsWeekForecast: {
    width: 92,
    alignItems: 'flex-end',
  },
  trendsWeekForecastLabel: {
    fontSize: 9,
    color: G.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  trendsWeekForecastValue: {
    fontSize: 13,
    fontWeight: '700',
    color: G.textPri,
    marginTop: 1,
  },
  trendsWeekCmp: {
    fontSize: 9,
    color: G.radarText,
    marginTop: 2,
    textAlign: 'right',
  },
  // ── Radar hint ──
  radarHint: {
    paddingVertical: 8,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: G.divider,
  },
  radarHintText: {
    fontSize: 11,
    fontWeight: '600',
    color: G.radarText,
    letterSpacing: 0.3,
  },
});
